/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { CSMasterListItemStatus } from '@gh/builder-shared-data';
import {
  combineReducers,
  composeSetters,
  EMPTY_TABLE,
  entityValidationSetter,
  fieldValidationSetter,
  getInvalidFields,
  isEntityInvalid,
  onChangeReducer,
  setDirty,
  setEntityField,
  setEntityFields,
  setNestedEntityField,
  setPristine,
  setUpdatedDirtyType,
  Table,
  toOnChangeReducer,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { arrayIterator } from '@gh/core-util';
import { find, first, flatten, fromPairs, keyBy, negate } from 'lodash';
import {
  getPbLinkCcElementId,
  getPbLinkCostCenterId,
  isPbLinkCsmRecordEmpty,
  PbLinkCcElement,
  pbLinkCcElementTable,
  pbLinkCcTable,
  PbLinkCsmGetResponse,
  PbLinkCsmRecord,
  pbLinkCsmRecordFromRef,
  pbLinkCsmRecordToRef,
  PbLinkRecord,
  pbLinkRecordTable,
  validatePbLinkCcElementField,
  validatePbLinkCsmRecord,
} from '../../resources/price-book-link.model';
import {
  PriceBookLinkCsmState,
  PriceBookLinkStateEvent,
  PriceBookLinkStateEventType,
  queryPbLinkCcElement,
  queryPbLinkCcInvalidElements,
  queryPbLinkCostCenters, queryPbLinkCsm,
  queryPbLinkCurrentRecords,
  queryPbLinkFirstCcElement,
  queryPbLinkSelectedCostCenter,
  queryPbLinkSelectedCostCenterId,
  queryPbLinkSelectedElement, queryPbLinkShowDeleted,
} from './price-book-link.state';

const mainFlowReducer = onChangeReducer(
  combineReducers({
    currentRecords: currentRecordsReducer,
  }),
  toOnChangeReducer(syncRefReducer),
  toOnChangeReducer(dirtyFlowReducer),
  toOnChangeReducer(globalFlagsReducer),
);

/**
 * Setter for first-order fields
 */
const setMainRecordField = (entity: PbLinkCsmRecord, field: keyof PbLinkCsmRecord, value: any): PbLinkCsmRecord => {
  switch (field) {
    case 'heading':
      return setNestedEntityField(entity, ['heading', 'headingId'], value);
    case 'subheading':
      return setNestedEntityField(entity, ['subheading', 'subheadingId'], value);
    case 'item':
      return setNestedEntityField(entity, ['item', 'itemId'], value);
    default:
      return setEntityField(entity, field, value);
  }
};

/**
 * Setter for dependent fields, called only when first-order field has changed
 */
const setDependentRecordField = (entity: PbLinkCsmRecord, field: keyof PbLinkCsmRecord, value: any) => {
  switch (field) {
    case 'heading':
      return setEntityFields(entity, {
        'subheading': setEntityFields(entity.subheading, {
          headingId: value,
          subheadingId: void 0,
        }),
        'item': setEntityFields(entity.item, {
          headingId: value,
          subheadingId: void 0,
          itemId: void 0,
        }),
      });
    case 'subheading':
      return setEntityField(entity, 'item', setEntityFields(entity.item, {
        subheadingId: value,
        itemId: void 0,
      }));
    default:
      return entity;
  }
};

// atomic setter of records fields
// it guarantees that record is consistent
const setRecordField = composeSetters(
  setMainRecordField,
  setDependentRecordField,
  setUpdatedDirtyType,
  entityValidationSetter(validatePbLinkCsmRecord));

const setElementField = composeSetters(
  setEntityField,
  setDirty,
  fieldValidationSetter(validatePbLinkCcElementField));

export function priceBookLinkCsmReducer(state: PriceBookLinkCsmState,
                                        event: PriceBookLinkStateEvent): PriceBookLinkCsmState {
  switch (event.type) {
    case PriceBookLinkStateEventType.Load:
      return loadDetails(state, <PbLinkCsmGetResponse>event.init, event.mode);
    case PriceBookLinkStateEventType.LoadCostCenter:
      return loadCostCenter(state, event.costCenterId, event.elements);
    case PriceBookLinkStateEventType.SelectCostCenter:
    case PriceBookLinkStateEventType.SelectElement:
    case PriceBookLinkStateEventType.ResetRecordFocusedCell:
    case PriceBookLinkStateEventType.NavigateToInvalid:
      return selectionReducer(state, event);
    case PriceBookLinkStateEventType.SetRecordField:
      return mainFlowReducer(state, event);
    case PriceBookLinkStateEventType.ResetDirty:
      return setPristine(state);
    case PriceBookLinkStateEventType.ToggleShowDeleted:
      return filterReducer(state, event);
    default:
      return state;
  }
}

export function dirtyFlowReducer(state: PriceBookLinkCsmState,
                                 event: PriceBookLinkStateEvent): PriceBookLinkCsmState {
  switch (event.type) {
    case PriceBookLinkStateEventType.SetRecordField: {
      const element = queryPbLinkSelectedElement(state);
      const costCenter = queryPbLinkSelectedCostCenter(state);
      if (element && costCenter) {
        return setEntityFields(state, {
          costCenters: pbLinkCcTable.update(state.costCenters, setDirty(costCenter)),
          elements: pbLinkCcElementTable.update(state.elements, setDirty(element)),
        });
      }
      return state;
    }
    default:
      return state;
  }
}

function currentRecordsReducer(records: Table<PbLinkRecord>,
                               event: PriceBookLinkStateEvent): Table<PbLinkRecord> {
  switch (event.type) {
    case PriceBookLinkStateEventType.SetRecordField:
      return pbLinkRecordTable.mergeById(
        records,
        event.recordId,
        (record: PbLinkCsmRecord) => setRecordField(record, event.name, event.value));
    default:
      return records;
  }
}

/**
 * Update list of references in current element if any of records was changed
 */
function syncRefReducer(state: PriceBookLinkCsmState, event: PriceBookLinkStateEvent): PriceBookLinkCsmState {
  const element = queryPbLinkSelectedElement(state);
  return element ?
    setEntityField(
      state,
      'elements',
      pbLinkCcElementTable.update(state.elements, setElementField(
        element,
        'csmLayout',
        queryPbLinkCurrentRecords(state).filter(negate(isPbLinkCsmRecordEmpty)).map(pbLinkCsmRecordToRef))))
    : state;
}

function globalFlagsReducer(state: PriceBookLinkCsmState, event: PriceBookLinkStateEvent): PriceBookLinkCsmState {
  switch (event.type) {
    case PriceBookLinkStateEventType.SetRecordField:
      return setDirty(state);
    default:
      return state;
  }
}

function filterReducer(state: PriceBookLinkCsmState, event: PriceBookLinkStateEvent): PriceBookLinkCsmState {
  switch (event.type) {
    case PriceBookLinkStateEventType.ToggleShowDeleted:
      return setShowDeleted(state, !state.filter.isShowDeleted);
    default:
      return state;
  }
}

function selectionReducer(state: PriceBookLinkCsmState, event: PriceBookLinkStateEvent): PriceBookLinkCsmState {
  switch (event.type) {
    case PriceBookLinkStateEventType.SelectCostCenter:
      return selectCostCenter(state, event.costCenterId);
    case PriceBookLinkStateEventType.SelectElement:
      return selectElement(state, event.elementId);
    case PriceBookLinkStateEventType.ResetRecordFocusedCell:
      return setNestedEntityField(state, ['selection', 'recordFocusedCell'], void 0);
    case PriceBookLinkStateEventType.NavigateToInvalid:
      return navigateToInvalid(state);
    default:
      return state;
  }
}

function navigateToInvalidElement(state: PriceBookLinkCsmState, invalid: PbLinkCcElement): PriceBookLinkCsmState {
  const firstInvalidRef = first(invalid.csmLayout.filter(isEntityInvalid));
  if (firstInvalidRef) {
    const invalidField = first(getInvalidFields(firstInvalidRef));
    const { csMasterId } = firstInvalidRef;
    const stateWithFocusedField = invalidField ? focusTemplateCell(
      selectElement(selectCostCenter(state, invalid.costCenterId), getPbLinkCcElementId(invalid)),
      { id: csMasterId, field: invalidField }) : state;
    const csm = queryPbLinkCsm(state, csMasterId);
    // show removed CSM if it was hidden
    if (!queryPbLinkShowDeleted(state) && csm && csm.status === CSMasterListItemStatus.Inactive) {
      return setShowDeleted(stateWithFocusedField, true);
    } else {
      return stateWithFocusedField;
    }
  } else {
    return state;
  }
}

function navigateToInvalid(state: PriceBookLinkCsmState): PriceBookLinkCsmState {
  const selectedElement = queryPbLinkSelectedElement(state);
  const selectedCostCenterId = queryPbLinkSelectedCostCenterId(state);

  // try to find invalid reference in the current selected element
  if (selectedElement && isEntityInvalid(selectedElement)) {
    return navigateToInvalidElement(state, selectedElement);
  }

  // try to find invalid element in the current selected cost center
  if (selectedCostCenterId) {
    const invalidElement = first(queryPbLinkCcInvalidElements(state, selectedCostCenterId));
    if (invalidElement) {
      return navigateToInvalidElement(state, invalidElement);
    }
  }

  const invalidCc = find(
    queryPbLinkCostCenters(state),
    (cc) => queryPbLinkCcInvalidElements(state, getPbLinkCostCenterId(cc)).length > 0);
  const invalid = invalidCc ? first(queryPbLinkCcInvalidElements(state, getPbLinkCostCenterId(invalidCc))) : void 0;
  return invalid ? navigateToInvalidElement(state, invalid) : state;
}

function focusTemplateCell(state: PriceBookLinkCsmState, focusedCell?: FocusedCell): PriceBookLinkCsmState {
  return setNestedEntityField(state, ['selection', 'recordFocusedCell'], focusedCell);
}

function selectElement(state: PriceBookLinkCsmState, elementId?: ID): PriceBookLinkCsmState {
  const stateWithSelectedElement = setNestedEntityField(state, ['selection', 'elementId'], elementId);

  return setEntityField(
    stateWithSelectedElement,
    'currentRecords',
    elementId ? buildRecordTable(state, elementId) : EMPTY_TABLE);
}

function selectCostCenter(state: PriceBookLinkCsmState, costCenterId: ID): PriceBookLinkCsmState {
  const stateWithSelectedCostCenter = setNestedEntityField(state, ['selection', 'costCenterId'], costCenterId);
  const firstElement = queryPbLinkFirstCcElement(stateWithSelectedCostCenter, costCenterId);
  return selectElement(stateWithSelectedCostCenter, firstElement ? getPbLinkCcElementId(firstElement) : void 0);
}

function loadCostCenter(state: PriceBookLinkCsmState,
                        costCenterId: ID,
                        elements: PbLinkCcElement[]): PriceBookLinkCsmState {
  return setEntityFields(state, {
    costCenters: pbLinkCcTable.mergeById(
      state.costCenters,
      costCenterId,
      (costCenter) => ({ ...costCenter, preloaded: true })),
    elements: pbLinkCcElementTable.insertAll(state.elements, elements),
  });
}

function loadDetails(state: PriceBookLinkCsmState,
                     init: PbLinkCsmGetResponse,
                     formMode?: FormMode): PriceBookLinkCsmState {
  const { priceBookId, priceBookName, csmLayout } = init;
  const headings = flatten(csmLayout.map((layout) => layout.headings));
  const subheadings = flatten(headings.map((heading) => heading.subheadings));
  const items = flatten(subheadings.map((subheading) => subheading.items));
  const subheadingToHeading = fromPairs(flatten(headings.map((heading) =>
    heading.subheadings.map((subheading) => [subheading.id, heading]))));
  const itemToSubheading = fromPairs(flatten(subheadings.map((subheading) =>
    subheading.items.map((item) => [item.id, subheading]))));

  return {
    ...state,
    formMode: formMode || state.formMode,
    priceBookId,
    masterInfo: { priceBookName },
    costCenters: pbLinkCcTable.create(arrayIterator(init.costCenters)),
    elements: pbLinkCcElementTable.create(),
    masters: keyBy(csmLayout, 'id'),
    headings: keyBy(headings, 'id'),
    subheadings: keyBy(subheadings, 'id'),
    items: keyBy(items, 'id'),
    subheadingToHeading,
    itemToSubheading,
    isDirty: false,
  };
}

function buildRecordTable(state: PriceBookLinkCsmState, elementId: ID): Table<PbLinkCsmRecord> {
  const { masters } = state;
  const element = queryPbLinkCcElement(state, elementId);
  if (element) {
    const { csmLayout } = element;
    // Construct array of records for all available templates.
    // If element does not have ref to some template, record is generated without paragraph and section id
    const records = Object.keys(masters).map((csMasterId) => {
      const csm = masters[csMasterId];
      return pbLinkCsmRecordFromRef(
        csMasterId,
        csm.name,
        csm.status === CSMasterListItemStatus.Active,
        find(csmLayout, { csMasterId }));
    });
    return <Table<PbLinkCsmRecord>>pbLinkRecordTable.create(arrayIterator(records));
  } else {
    return EMPTY_TABLE;
  }
}

function setShowDeleted(state: PriceBookLinkCsmState, isShowDeleted: boolean): PriceBookLinkCsmState {
  return setNestedEntityField(state, ['filter', 'isShowDeleted'], isShowDeleted);
}
